import { firebaseStorage } from "../firebaseConfig";
import {Dialog, PromptResult} from '@capacitor/dialog';
import {Toast} from '@capacitor/toast';

import { AssessmentPayloadProps, AssessmentCreationFeedBackUIProps, AssessmentListUIProps, FirebaseUserProps } from "./entityUtilities";
import {initiateProcessing, setAssessment, setVideo} from './apiSetUtilities';
import { AssessmentSchemaProps } from "./mongoSchemas";
import { refreshToken } from "../context/UserContext";
import { getAssesmentById, getLatestAssesment, getSubjectFromId } from "./apiGetUtilities";
import { FastRewind } from "@material-ui/icons";

import { useTranslation } from "react-i18next";
import { VideoFile } from "@mui/icons-material";
import { debugLog } from '../common/debugUtilities';

export const showDialog = async(title:any,message:any,oktitle:any, canceltitle:any)=> {
const {value} = await Dialog.confirm({
    title: title,
    message: message,
    okButtonTitle: oktitle,
    cancelButtonTitle: canceltitle
});

return value;

};

export const showAlert = async (title: any, message:any, buttonTitle: any):Promise<boolean> =>{
await Dialog.alert( {title: title, message: message, buttonTitle: buttonTitle} );
return true;
};
  
export const showPrompt = async (title: any, message: any): Promise<PromptResult> => {

const {value, cancelled} = await Dialog.prompt({
    title: title,
    message: message
});
const result: PromptResult = {value,cancelled};

return result;

};


export const showToast = async (msg: string) => {

    debugLog('Showing toast');
    await Toast.show({
      text: msg
    });
  };

export const checkQuota = async (firebaseUser:FirebaseUserProps|undefined, subjectId: string|undefined):Promise<boolean> => {

  if(firebaseUser){
    const latest:AssessmentSchemaProps|undefined = await getLatestAssesment(firebaseUser, subjectId);
    debugLog('Latest asmt:', latest);
    if (latest == undefined) {
      return true; // no asmts for this subject yet
    }
    if(latest?.createdAt){
      const asmtUpdatedDate = new Date(latest?.createdAt);
      const currentDate = new Date(Date.now());
      const hours = Math.abs(asmtUpdatedDate.getTime() - currentDate.getTime()) / 3600000;
      debugLog('Hours elapsed:', hours);
      if (hours < 24.0){
        return false;
      }
      else {
        return true;
      }
    }

    return false;

  }
  return false;
}

// Validate video files before upload
export const validateVideoFile = (file: File): { valid: boolean; error?: string } => {
  const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB limit
  const MIN_FILE_SIZE = 100 * 1024; // 100KB minimum (sanity check)
  const ALLOWED_VIDEO_TYPES = [
    'video/mp4',
    'video/quicktime', // .mov files
    'video/x-msvideo', // .avi files
    'video/webm'
  ];

  // Check if file exists
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  // Check file type
  if (!file.type.startsWith('video/')) {
    return {
      valid: false,
      error: `Invalid file type: ${file.type}. Please select a video file.`
    };
  }

  // Check if it's an allowed video type
  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    console.warn(`Video type ${file.type} not in allowed list, but will allow since it starts with video/`);
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    const maxSizeGB = (MAX_FILE_SIZE / (1024 * 1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File too large: ${sizeMB}MB. Maximum file size is ${maxSizeGB}GB.`
    };
  }

  if (file.size < MIN_FILE_SIZE) {
    return {
      valid: false,
      error: 'File too small. The video file appears to be corrupted or empty.'
    };
  }

  // Check filename
  if (!file.name || file.name.length === 0) {
    return {
      valid: false,
      error: 'Invalid filename'
    };
  }

  // All validations passed
  debugLog(`Video file validated: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)}MB, ${file.type})`);
  return { valid: true };
};

export async function createAssessmentAndDeliverFeedback(firebaseUser:FirebaseUserProps|undefined,
  asmtCreationPayload:AssessmentPayloadProps, setStatus:(val:any)=>void, t: any, authLevel: number = 0, setUploadState?: (state: any) => void):Promise<void>{

  if(!firebaseUser) return undefined;
  debugLog('Create Assessment with feedback:', asmtCreationPayload);
  debugLog('User auth level:', authLevel);

  // Validate all video files before starting upload
  for (const video of asmtCreationPayload.videosForUpload) {
    const validation = validateVideoFile(video);
    if (!validation.valid) {
      await showAlert('Invalid Video File', validation.error || 'Please select a valid video file', 'OK');
      return undefined;
    }
  }

  const subjectOwnerId = firebaseUser.uid;
  const videoUploadTasks = await uploadFilesToFirebaseWithFeedBack(firebaseUser, subjectOwnerId,
    asmtCreationPayload.videosForUpload, setStatus, setUploadState);
  const totalVideos = asmtCreationPayload.videosForUpload.length;
  // we be done
  let data:AssessmentCreationFeedBackUIProps = {
    bytesTransferred:0,
    totalSize:0,
    fileName: '',
    videoTotal:totalVideos,
    currentVideoCount:totalVideos,

    genericMessage:t('processing',{ns:'utils'})
  };
  setStatus(data);
  // For all the promises at once use this...
  // const videoUploadTasks = await Promise.all(videoUploadPromises);
  // Uploading is done, take the data and create the assessment

  const fullUrl = await firebaseStorage.ref(videoUploadTasks[0]?.metadata.fullPath).getDownloadURL();
  debugLog('Full URL of uploaded vid:', fullUrl);
  
  const asmtDbPayload:AssessmentSchemaProps = {
    date:asmtCreationPayload.date,
    subjectId:asmtCreationPayload.subjectId,
    video: fullUrl
  };

  // let asmtData = <AssessmentSchemaProps>{};


  if (!asmtCreationPayload.assessmentId) {
    const asmtData = await setAssessment(firebaseUser, asmtDbPayload) as AssessmentSchemaProps;
    debugLog('Asmt created:', asmtData);
    if(!asmtData){
      debugLog('Failure with asmt');
      //hmm failure
      return undefined;
    }
    else {
      debugLog('Created asmt on API:', asmtData);
      // New assessment created...
      const subjectDetails = await getSubjectFromId(firebaseUser, asmtData?.subjectId);
      debugLog('Subject details:', subjectDetails);
      let duedate = subjectDetails?.duedate;
      debugLog('Duedate:', duedate);
      let videodate = asmtData.date;

      if (duedate != null && videodate != null){
        const hoursInWeek = 7 * 24 ;
        const weeks =  (new Date(videodate).getTime() - new Date(duedate).getTime()) / (3600000 * hoursInWeek);
        let adjustedWeeks = weeks < 0? 0: weeks;
        debugLog('Weeks at video time:', adjustedWeeks);
        adjustedWeeks = Math.floor(adjustedWeeks+0.49);
        debugLog('Rounded Weeks at video time:', adjustedWeeks);
        let newasmtData = {...asmtData, additionalFlags: {"age": adjustedWeeks}};

        // Auto-initiate processing only for non-admin users
        // Admin users (authLevel >= 5) can manually trigger processing via restart button
        if (authLevel < 5) {
          debugLog('Initiating automatic processing for regular user');
          const result = initiateProcessing(firebaseUser, newasmtData);
          debugLog('Kicked off processing:', result);
        } else {
          debugLog('Skipping automatic processing for admin user (authLevel:', authLevel, ')');
          debugLog('Admin can manually trigger processing via restart button');
        }

      }
    }
  }
  else{
   debugLog('Existing assessment?');
  }


  let count = 1;
  data = {
    bytesTransferred:1,
    totalSize:1,
    fileName: '',
    videoTotal:count,
    currentVideoCount:0,  
    genericMessage:t('asmtcomplete',{ns:'utils'})
  };
  setStatus(data);
  // hmm is this asmt for UI ever used? seems like once this 
  // function returns a full reset is done on the list
  // and the entities are all created. Will keep around for future
  // optimization options 
  return ;
}


//
// Upload videos, create observations, create assessment
// If assessment payload contains an assessmentId, then, don't create an assessment -- just do the rest
//
async function uploadFilesToFirebaseWithFeedBack(firebaseUser:FirebaseUserProps|undefined,
  subjectOwnerId:string, videosForUpload:any[],
  setStatus:(val:any)=>void, setUploadState?: (state: any) => void)
  :Promise<firebase.default.storage.UploadTaskSnapshot[]>{

  if(!firebaseUser) return [];
  debugLog('Create Assessment with feedback');
  await refreshToken(firebaseUser);
  const bucketPath = '__videos';
  const baseBucketPath = bucketPath + '/' + subjectOwnerId;
  debugLog('Uploading to:', baseBucketPath);

  const videoUploadPromises = [];
  let videoUploadTasks:firebase.default.storage.UploadTaskSnapshot[] = [];
  const totalVideos = videosForUpload.length;
  let currentVideoCount = 1;

  // Upload the videos first, get their storage information for later usage
  for(const video of videosForUpload){
    // Make the storage filename unique by appending an ISO-8601 timestamp,
    // so re-uploads (or admin uploads of similarly-named files for different
    // parents) don't silently overwrite an existing blob at the same path.
    // Format: '<basename>_<YYYY-MM-DDTHH-MM-SS-mmmZ>.<ext>'
    const ts = new Date().toISOString().replace(/[:.]/g, '-'); // colons/dots aren't great in storage paths
    const dot = video.name.lastIndexOf('.');
    const stampedName = dot > 0
      ? `${video.name.slice(0, dot)}_${ts}${video.name.slice(dot)}`
      : `${video.name}_${ts}`;
    const bucketLocation = baseBucketPath + '/' + stampedName;

    var metadata = {
      contentType: 'video/mp4',
      customMetadata: {
        'uploadedBy': firebaseUser.uid,
        'uploadedAt': new Date().toISOString()
      }
    };

    debugLog('Starting upload for:', video.name, `(${(video.size / (1024 * 1024)).toFixed(2)} MB)`);

    // Track upload progress with speed and ETA
    let startTime = Date.now();
    let lastBytesTransferred = 0;
    let lastUpdateTime = Date.now();
    let uploadSpeed = 0; // Persist last known speed
    let etaSeconds = 0;  // Persist last known ETA

    const uploadTask = firebaseStorage.ref(bucketLocation).put(video, metadata);

    videoUploadPromises.push(uploadTask);

    uploadTask.on('state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        const currentTime = Date.now();
        const timeDiff = (currentTime - lastUpdateTime) / 1000; // seconds
        const bytesDiff = snapshot.bytesTransferred - lastBytesTransferred;

        // Calculate upload speed (MB/s) - update every 500ms minimum
        // Only update speed/ETA when we have enough time difference
        if (timeDiff > 0.5) {
          uploadSpeed = (bytesDiff / timeDiff) / (1024 * 1024);

          // Calculate ETA
          const bytesRemaining = snapshot.totalBytes - snapshot.bytesTransferred;
          etaSeconds = uploadSpeed > 0 ? bytesRemaining / (uploadSpeed * 1024 * 1024) : 0;

          lastBytesTransferred = snapshot.bytesTransferred;
          lastUpdateTime = currentTime;
        }

        const data: AssessmentCreationFeedBackUIProps = {
          bytesTransferred: snapshot.bytesTransferred,
          totalSize: snapshot.totalBytes,
          fileName: video.name,
          videoTotal: totalVideos,
          currentVideoCount: currentVideoCount,
          uploadSpeed: uploadSpeed, // Use persisted value
          eta: etaSeconds,           // Use persisted value
          genericMessage: ''
        };

        setStatus(data);

        // Update global upload state directly for header indicator
        if (setUploadState) {
          const perc = Math.floor(progress);
          setUploadState({
            isUploading: true,
            percentage: perc,
            fileName: video.name
          });
        }

        // Log progress periodically
        if (progress % 10 < 1) {
          debugLog(`Upload ${video.name}: ${progress.toFixed(1)}% (${uploadSpeed.toFixed(2)} MB/s)`);
        }
      },
      (error) => {
        // Enhanced error handling
        console.error('Upload error for', video.name, ':', error);

        let errorMessage = 'Upload failed';

        if (error.code === 'storage/canceled') {
          errorMessage = 'Upload was canceled';
          debugLog('User canceled upload');
        } else if (error.code === 'storage/unauthorized') {
          errorMessage = 'Permission denied - please sign in again';
          console.error('User does not have permission to upload');
        } else if (error.code === 'storage/retry-limit-exceeded') {
          errorMessage = 'Network connection unstable - please check your connection and try again';
          console.error('Upload retry limit exceeded - network may be unstable');
        } else if (error.code === 'storage/quota-exceeded') {
          errorMessage = 'Storage quota exceeded';
          console.error('Storage quota exceeded');
        } else if (error.code === 'storage/unauthenticated') {
          errorMessage = 'Authentication expired - please sign in again';
          console.error('User is not authenticated');
        } else {
          errorMessage = `Upload failed: ${error.message}`;
          console.error('Unknown upload error:', error.message, error.code);
        }

        setStatus({
          bytesTransferred: 0,
          totalSize: video.size,
          fileName: video.name,
          videoTotal: totalVideos,
          currentVideoCount: currentVideoCount,
          genericMessage: errorMessage,
          error: error
        });

        // Reset upload indicator on error
        if (setUploadState) {
          setUploadState({
            isUploading: false,
            percentage: 0,
            fileName: ''
          });
        }
      },
      () => {
        // Upload completed successfully
        const elapsedTime = (Date.now() - startTime) / 1000;
        debugLog(`Upload complete for ${video.name} - Total time: ${elapsedTime.toFixed(2)}s`);

        // Reset upload indicator when upload completes
        if (setUploadState) {
          setUploadState({
            isUploading: false,
            percentage: 0,
            fileName: ''
          });
        }
      }
    );

    try {
      // Wait for this video to complete before starting next
      const result = await Promise.all(videoUploadPromises);
      //if success remove one, the result might be what is left in the promise array...
      videoUploadPromises.shift();
      videoUploadTasks = videoUploadTasks.concat(result);
      currentVideoCount += 1;
    } catch (error) {
      console.error('Upload failed for:', video.name, error);
      // Re-throw to propagate error up
      throw error;
    }
  }
  return videoUploadTasks;
}

export async function reprocessVideo(firebaseUser:FirebaseUserProps|undefined, asmtid:string, skipPoseInference:boolean = false) {
  if(!firebaseUser) return;
  debugLog('Reprocessing video, skipPoseInference:', skipPoseInference);

  const asmtPayload = await getAssesmentById(firebaseUser, asmtid) as AssessmentSchemaProps;

  const subjectDetails = await getSubjectFromId(firebaseUser, asmtPayload?.subjectId);
  debugLog('Subject details:', subjectDetails);
  let duedate = subjectDetails?.duedate;
  debugLog('Duedate:', duedate);
  let videodate = asmtPayload.date;

  if (duedate != null && videodate != null){
    const hoursInWeek = 7 * 24 ;
    const weeks =  (new Date(videodate).getTime() - new Date(duedate).getTime()) / (3600000 * hoursInWeek);
    let adjustedWeeks = weeks < 0? 0: weeks;
    debugLog('Weeks at video time:', adjustedWeeks);
    adjustedWeeks = Math.floor(adjustedWeeks+0.49);
    debugLog('Rounded Weeks at video time:', adjustedWeeks);

    const additionalFlags: {age: number, skipPoseInference?: boolean} = {"age": adjustedWeeks};
    if (skipPoseInference) {
      additionalFlags.skipPoseInference = true;
    }
    let newasmtData = {...asmtPayload, additionalFlags};

    // initiate processing
    debugLog('Initiate process with:', newasmtData);
    const result = initiateProcessing(firebaseUser, newasmtData);
    debugLog('Kicked off processing:', result);
  }


}

export  function deleteCloudVideo(firebaseUser: FirebaseUserProps|undefined, videoUrl:string) {
  debugLog('deleteCloudVideo:', videoUrl);
  //const storageRef =  firebaseStorage.ref(videoUrl).child(videoUrl);
  const storageRef =  firebaseStorage.refFromURL(videoUrl);
  storageRef.delete().then( ()=> {
    debugLog('Cloud Video file deleted');
  }).catch ((error) => {
    debugLog('Error deleting cloud file:', error);
  })

}

export function sleep(ms:number) {
return new Promise(resolve => setTimeout(resolve, ms));
}

